import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Users, ChefHat, Link2, Calendar as CalIcon, AlertCircle, Repeat, MapPin, ChevronLeft, ChevronRight, X, Clock3, KeyRound, Sprout } from "lucide-react";
import { AddUserDialog, DeleteUserButton, BulkDeleteBar, SelectCheckbox } from "@/components/UserManagement";
import EditCustomerDialog from "@/components/EditCustomerDialog";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import ChefAvailability from "@/components/ChefAvailability";
import LeadsManagement from "@/components/LeadsManagement";
import ChefManagement from "@/components/ChefManagement";
import LeaveManagement from "@/components/LeaveManagement";
import AdminsTab from "@/components/AdminsTab";
import { useAuth } from "@/context/AuthContext";

const ALL = "all";
const SERVICES = ["breakfast", "lunch", "dinner", "full-day"];
const PERIOD_PRESETS = [
  { id: "1w", label: "1 week", days: 6 },
  { id: "2w", label: "2 weeks", days: 13 },
  { id: "1m", label: "1 month", days: 29 },
  { id: "custom", label: "Custom", days: null },
];

// ----- date helpers -----
const ymd = (d) => {
  const x = new Date(d);
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${x.getFullYear()}-${m}-${day}`;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => {
  const x = new Date(d); const day = x.getDay() || 7; // Mon=1..Sun=7
  x.setDate(x.getDate() - (day - 1));
  x.setHours(0, 0, 0, 0);
  return x;
};
const overlaps = (s1, e1, s2, e2) => s1 <= e2 && s2 <= e1;
const svcList = (m) => Array.isArray(m.service_types) ? m.service_types : (m.service_type ? [m.service_type] : []);

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="card-default">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</div>
          <div className="heading text-3xl font-bold mt-2">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center"><Icon size={18} className="text-primary"/></div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [slotReqs, setSlotReqs] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [chefLocs, setChefLocs] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [chefChanges, setChefChanges] = useState([]);
  const [custSelected, setCustSelected] = useState({});
  const [editCustomer, setEditCustomer] = useState(null);

  const today = ymd(new Date());

  // Mapping form
  const [form, setForm] = useState({
    customer_id: "", chef_id: "", slot: "07:00-08:00",
    service_types: ["breakfast"], start_date: today, end_date: ymd(addDays(today, 6)),
    customer_address: "", period: "1w",
  });

  // Filters for "All mappings"
  const [filters, setFilters] = useState({ chef_id: ALL, customer_id: ALL, slot: "", date_from: "", date_to: "" });

  // Calendar tab state
  const [calChef, setCalChef] = useState(ALL);
  const [calWeekStart, setCalWeekStart] = useState(ymd(startOfWeek(new Date())));

  const loadStatic = useCallback(async () => {
    try {
      const [s, c, ch, cp, sr, lv, ccr] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: { role: "customer" }}),
        api.get("/admin/users", { params: { role: "chef" }}),
        api.get("/admin/complaints"),
        api.get("/admin/slot-requests"),
        api.get("/admin/leaves"),
        api.get("/admin/chef-change-requests"),
      ]);
      setStats(s.data); setCustomers(c.data); setChefs(ch.data);
      setComplaints(cp.data); setSlotReqs(sr.data); setLeaves(lv.data); setChefChanges(ccr.data);
    } catch { toast.error("Failed to load admin data"); }
  }, []);

  const loadMappings = useCallback(async () => {
    const params = {};
    if (filters.chef_id !== ALL) params.chef_id = filters.chef_id;
    if (filters.customer_id !== ALL) params.customer_id = filters.customer_id;
    if (filters.slot) params.slot = filters.slot;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    try {
      const { data } = await api.get("/admin/mappings", { params });
      setMappings(data);
    } catch {}
  }, [filters]);

  const loadCalendar = useCallback(async () => {
    const params = {
      date_from: calWeekStart,
      date_to: ymd(addDays(calWeekStart, 6)),
    };
    if (calChef !== ALL) params.chef_id = calChef;
    try { const { data } = await api.get("/admin/calendar", { params }); setCalendar(data); } catch {}
  }, [calChef, calWeekStart]);

  const loadChefLocs = useCallback(async () => {
    try { const { data } = await api.get("/admin/chef-locations"); setChefLocs(data); } catch {}
  }, []);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { loadMappings(); }, [loadMappings]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);
  useEffect(() => {
    loadChefLocs();
    const t = setInterval(loadChefLocs, 30000);
    return () => clearInterval(t);
  }, [loadChefLocs]);

  // Auto-fill address & end_date
  const onCustomerChange = (id) => {
    const c = customers.find(x => x.id === id);
    setForm(f => ({ ...f, customer_id: id, customer_address: c?.profile?.address || "" }));
  };
  const onPeriodChange = (period) => {
    if (period === "custom") { setForm(f => ({ ...f, period })); return; }
    const preset = PERIOD_PRESETS.find(p => p.id === period);
    setForm(f => ({ ...f, period, end_date: ymd(addDays(f.start_date, preset.days)) }));
  };
  const onStartDateChange = (start_date) => {
    setForm(f => {
      const preset = PERIOD_PRESETS.find(p => p.id === f.period);
      const end_date = preset && preset.days != null ? ymd(addDays(start_date, preset.days)) : f.end_date;
      return { ...f, start_date, end_date };
    });
  };

  const createMapping = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.chef_id) { toast.error("Pick a customer and a chef"); return; }
    try {
      await api.post("/admin/mappings", {
        customer_id: form.customer_id, chef_id: form.chef_id, slot: form.slot,
        service_types: form.service_types, start_date: form.start_date, end_date: form.end_date,
        customer_address: form.customer_address,
      });
      toast.success("Mapping created");
      loadMappings(); loadCalendar(); loadStatic();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateMapping = async (mid, patch) => {
    try { await api.patch(`/admin/mappings/${mid}`, patch); loadMappings(); loadCalendar(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleActive = async (uid, is_active) => { await api.patch(`/admin/users/${uid}`, { is_active }); loadStatic(); };
  const resolveComplaint = async (id) => { await api.patch(`/admin/complaints/${id}`, { status: "resolved" }); loadStatic(); };
  const decideSlotReq = async (id, status) => { await api.patch(`/admin/slot-requests/${id}`, { status }); loadStatic(); };
  const decideLeave = async (id, status) => { await api.patch(`/admin/leaves/${id}`, { status }); loadStatic(); };
  const decideChefChange = async (id, status) => { await api.patch(`/admin/chef-change-requests/${id}`, { status }); loadStatic(); };

  // Calendar: build week grid
  const calendarDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(calWeekStart, i)), [calWeekStart]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="heading text-3xl font-bold">Admin control room</h1>
        <p className="text-muted-foreground">Map chefs, manage customers, resolve issues.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <StatCard label="Customers" value={stats.customers ?? 0} icon={Users}/>
          <StatCard label="Chefs" value={stats.chefs ?? 0} icon={ChefHat}/>
          <StatCard label="Active mappings" value={stats.active_mappings ?? 0} icon={Link2}/>
          <StatCard label="Open complaints" value={stats.open_complaints ?? 0} icon={AlertCircle}/>
        </div>

        <Tabs defaultValue="leads" className="mt-8">
          <TabsList className="bg-muted rounded-full p-1 flex-wrap h-auto">
            <TabsTrigger value="leads" className="rounded-full" data-testid="admin-tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="availability" className="rounded-full" data-testid="admin-tab-availability">Chef Assignment</TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-full" data-testid="admin-tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="livemap" className="rounded-full" data-testid="admin-tab-livemap">Live map</TabsTrigger>
            <TabsTrigger value="customers" className="rounded-full" data-testid="admin-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="chefs" className="rounded-full" data-testid="admin-tab-chefs">Chefs</TabsTrigger>
            <TabsTrigger value="complaints" className="rounded-full" data-testid="admin-tab-complaints">Complaints</TabsTrigger>
            <TabsTrigger value="slot-requests" className="rounded-full" data-testid="admin-tab-slotreq">Slot requests</TabsTrigger>
            <TabsTrigger value="chef-changes" className="rounded-full" data-testid="admin-tab-chefchange">Chef changes</TabsTrigger>
            <TabsTrigger value="leaves" className="rounded-full" data-testid="admin-tab-leaves">Leaves</TabsTrigger>
            <TabsTrigger value="admins" className="rounded-full" data-testid="admin-tab-admins">Admins</TabsTrigger>
          </TabsList>

          {/* MAPPING ----------------------------------------------------- */}
          <TabsContent value="mapping" className="mt-6 space-y-6">
            <form onSubmit={createMapping} className="card-default space-y-4" data-testid="mapping-form">
              <div className="flex items-center justify-between">
                <h3 className="heading text-lg">Assign chef to customer</h3>
                <Badge variant="outline" className="rounded-full">Bulk-assigns the entire subscription period</Badge>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label>Customer</Label>
                  <Select value={form.customer_id} onValueChange={onCustomerChange}>
                    <SelectTrigger data-testid="map-cust"><SelectValue placeholder="Select customer"/></SelectTrigger>
                    <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chef</Label>
                  <Select value={form.chef_id} onValueChange={v=>setForm({...form, chef_id:v})}>
                    <SelectTrigger data-testid="map-chef"><SelectValue placeholder="Select chef"/></SelectTrigger>
                    <SelectContent>{chefs.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Slot</Label>
                  <Input value={form.slot} onChange={e=>setForm({...form, slot:e.target.value})} placeholder="07:00-08:00" data-testid="map-slot"/>
                </div>
                <div>
                  <Label>Services <span className="text-muted-foreground text-xs">(pick one or more)</span></Label>
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="map-services">
                    {SERVICES.map(s => {
                      const on = form.service_types.includes(s);
                      return (
                        <button type="button" key={s} data-testid={`svc-${s}`}
                          onClick={()=>setForm(f=>({...f, service_types: on ? f.service_types.filter(x=>x!==s) : [...f.service_types, s]}))}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors capitalize ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"}`}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label>Subscription period</Label>
                  <Select value={form.period} onValueChange={onPeriodChange}>
                    <SelectTrigger data-testid="map-period"><SelectValue/></SelectTrigger>
                    <SelectContent>{PERIOD_PRESETS.map(p=><SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start date</Label>
                  <Input type="date" value={form.start_date} onChange={e=>onStartDateChange(e.target.value)} data-testid="map-start"/>
                </div>
                <div>
                  <Label>End date</Label>
                  <Input type="date" value={form.end_date} min={form.start_date}
                    onChange={e=>setForm({...form, end_date:e.target.value, period:"custom"})} data-testid="map-end"/>
                </div>
                <div className="flex items-end">
                  <Button type="submit" className="rounded-full bg-primary hover:bg-primary/90 w-full" data-testid="map-submit">Create mapping</Button>
                </div>
              </div>

              <div>
                <Label>Customer address</Label>
                <AddressAutocomplete
                  value={form.customer_address}
                  onChange={(v)=>setForm({...form, customer_address: v})}
                  onSelect={({ address })=>setForm({...form, customer_address: address })}
                  placeholder="Auto-filled from customer profile; editable"
                  multiline
                  data-testid="map-address"
                />
              </div>
            </form>

            {/* Filters */}
            <div className="card-default">
              <div className="flex items-center justify-between mb-4">
                <h3 className="heading text-lg">All mappings</h3>
                <Button variant="ghost" size="sm" onClick={()=>setFilters({ chef_id: ALL, customer_id: ALL, slot: "", date_from: "", date_to: "" })} data-testid="filter-clear">
                  <X size={14} className="mr-1"/> Clear filters
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                <div>
                  <Label className="text-xs">Chef</Label>
                  <Select value={filters.chef_id} onValueChange={v=>setFilters({...filters, chef_id:v})}>
                    <SelectTrigger data-testid="filter-chef"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>All chefs</SelectItem>
                      {chefs.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Customer</Label>
                  <Select value={filters.customer_id} onValueChange={v=>setFilters({...filters, customer_id:v})}>
                    <SelectTrigger data-testid="filter-customer"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>All customers</SelectItem>
                      {customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Slot contains</Label>
                  <Input value={filters.slot} onChange={e=>setFilters({...filters, slot:e.target.value})} placeholder="07:00" data-testid="filter-slot"/>
                </div>
                <div>
                  <Label className="text-xs">Date from</Label>
                  <Input type="date" value={filters.date_from} onChange={e=>setFilters({...filters, date_from:e.target.value})} data-testid="filter-from"/>
                </div>
                <div>
                  <Label className="text-xs">Date to</Label>
                  <Input type="date" value={filters.date_to} onChange={e=>setFilters({...filters, date_to:e.target.value})} data-testid="filter-to"/>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Chef</TableHead>
                    <TableHead>Slot</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappings.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No mappings match the filters.</TableCell></TableRow>}
                  {mappings.map(m => (
                    <TableRow key={m.id} data-testid={`row-${m.id}`}>
                      <TableCell className="font-medium">{m.customer_name}</TableCell>
                      <TableCell>
                        <Select value={m.chef_id} onValueChange={v=>updateMapping(m.id, { chef_id: v })}>
                          <SelectTrigger className="w-40 h-8"><SelectValue/></SelectTrigger>
                          <SelectContent>{chefs.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input defaultValue={m.slot} onBlur={e=>e.target.value!==m.slot && updateMapping(m.id, { slot: e.target.value })} className="w-28 h-8"/>
                      </TableCell>
                      <TableCell className="capitalize">
                        <div className="flex flex-wrap gap-1">
                          {svcList(m).map(s => (
                            <Badge key={s} variant="outline" className="rounded-full capitalize text-[10px] px-2 py-0.5">{s}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{m.start_date}</div>
                        <div className="text-muted-foreground">→ {m.end_date || "—"}</div>
                      </TableCell>
                      <TableCell className="text-xs max-w-[180px] truncate" title={m.customer_address}>{m.customer_address || "—"}</TableCell>
                      <TableCell><Badge className="rounded-full" variant={m.active ? "default" : "outline"}>{m.active ? "Yes" : "No"}</Badge></TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={()=>updateMapping(m.id, { active: !m.active })}>{m.active ? "Deactivate" : "Activate"}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CALENDAR ---------------------------------------------------- */}
          <TabsContent value="availability" className="mt-6">
            <ChefAvailability
              chefs={chefs}
              customers={customers}
              onAssigned={() => { loadMappings(); loadCalendar(); loadStatic(); }}
            />
          </TabsContent>

          <TabsContent value="calendar" className="mt-6">
            <div className="card-default">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2"><CalIcon size={18} className="text-primary"/><h3 className="heading text-xl">Chef calendar</h3></div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={()=>setCalWeekStart(ymd(addDays(calWeekStart, -7)))} data-testid="cal-prev"><ChevronLeft size={16}/> Prev</Button>
                  <Input type="date" value={calWeekStart} onChange={e=>setCalWeekStart(ymd(startOfWeek(e.target.value)))} className="w-44" data-testid="cal-week"/>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={()=>setCalWeekStart(ymd(addDays(calWeekStart, 7)))} data-testid="cal-next">Next <ChevronRight size={16}/></Button>
                  <Button size="sm" variant="ghost" onClick={()=>setCalWeekStart(ymd(startOfWeek(new Date())))} data-testid="cal-today">Today</Button>
                  <div className="w-44">
                    <Select value={calChef} onValueChange={setCalChef}>
                      <SelectTrigger data-testid="cal-chef"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL}>All chefs</SelectItem>
                        {chefs.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <div className="min-w-[920px]">
                  {/* day headers */}
                  <div className="grid grid-cols-[160px_repeat(7,1fr)] gap-px bg-border rounded-xl overflow-hidden text-sm">
                    <div className="bg-muted px-3 py-2 font-medium">Chef</div>
                    {calendarDays.map((d, i) => {
                      const isToday = ymd(d) === today;
                      return (
                        <div key={i} className={`px-3 py-2 font-medium ${isToday ? "bg-primary/10 text-primary" : "bg-muted"}`}>
                          <div className="text-xs uppercase tracking-wider">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                          <div className="heading">{d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}</div>
                        </div>
                      );
                    })}

                    {calendar.length === 0 && (
                      <div className="bg-card col-span-8 px-3 py-8 text-center text-muted-foreground">No chefs match this filter.</div>
                    )}

                    {calendar.map(row => (
                      <div key={row.chef.id} className="contents">
                        <div className="bg-card px-3 py-3">
                          <div className="font-medium text-sm">{row.chef.name}</div>
                          <div className="text-xs text-muted-foreground">{row.chef.email}</div>
                        </div>
                        {calendarDays.map((d, i) => {
                          const dStr = ymd(d);
                          const cellMaps = row.mappings.filter(m => overlaps(m.start_date, m.end_date || m.start_date, dStr, dStr));
                          return (
                            <div key={i} className="bg-card px-2 py-2 min-h-[72px] space-y-1">
                              {cellMaps.length === 0 ? <span className="text-xs text-muted-foreground">—</span> :
                                cellMaps.map(m => (
                                  <div key={m.id} className="text-[11px] leading-tight px-2 py-1 rounded-md bg-primary/10 text-primary border border-primary/20" title={`${m.customer_name} · ${svcList(m).join(", ")} · ${m.slot}`}>
                                    <div className="font-medium truncate">{m.slot}</div>
                                    <div className="truncate">{m.customer_name}</div>
                                    <div className="capitalize text-[10px] opacity-80 truncate">{svcList(m).join(", ")}</div>
                                  </div>
                                ))
                              }
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* LIVE MAP ---------------------------------------------------- */}
          <TabsContent value="livemap" className="mt-6">
            <div className="card-default">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><MapPin size={18} className="text-primary"/><h3 className="heading text-xl">Live chef locations</h3></div>
                <span className="text-xs text-muted-foreground">Auto-refreshes every 30s</span>
              </div>
              {chefLocs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No chefs to display.</p>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {chefLocs.map(row => (
                    <div key={row.chef.id} className="rounded-2xl border border-border overflow-hidden bg-card" data-testid={`livemap-${row.chef.id}`}>
                      <div className="p-4 flex items-center justify-between">
                        <div>
                          <div className="font-medium">{row.chef.name}</div>
                          <div className="text-xs text-muted-foreground">{row.chef.phone || row.chef.email}</div>
                        </div>
                        {row.location ? (
                          <Badge className="rounded-full bg-secondary text-secondary-foreground">Live</Badge>
                        ) : <Badge variant="outline" className="rounded-full">Offline</Badge>}
                      </div>
                      {row.location ? (
                        <>
                          <iframe
                            title={`map-${row.chef.id}`}
                            className="w-full h-44 border-t border-border"
                            src={`https://maps.google.com/maps?q=${row.location.lat},${row.location.lng}&z=15&output=embed`}
                          />
                          <div className="p-3 text-xs text-muted-foreground flex items-center justify-between">
                            <span>Lat {row.location.lat.toFixed(4)}, Lng {row.location.lng.toFixed(4)}</span>
                            <span>{new Date(row.location.updated_at).toLocaleTimeString()}</span>
                          </div>
                        </>
                      ) : (
                        <div className="px-4 pb-4 text-xs text-muted-foreground">Chef hasn't shared location yet today.</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* CUSTOMERS --------------------------------------------------- */}
          <TabsContent value="customers" className="mt-6">
            <div className="card-default">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="heading text-lg">All customers</h3>
                <AddUserDialog role="customer" label="Add customer" onCreated={loadStatic}/>
              </div>
              <BulkDeleteBar users={customers} selected={custSelected} setSelected={setCustSelected} onDeleted={loadStatic} label="customers"/>
              <Table>
                <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Address</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {customers.map(u => (
                    <TableRow key={u.id} className={custSelected[u.id] ? "bg-primary/5" : ""}>
                      <TableCell><SelectCheckbox id={u.id} selected={custSelected} setSelected={setCustSelected} testid={`cust-select-${u.id}`}/></TableCell>
                      <TableCell>{u.name}</TableCell><TableCell>{u.phone}</TableCell>
                      <TableCell className="text-xs max-w-[240px] truncate" title={u.profile?.address}>{u.profile?.address || "—"}</TableCell>
                      <TableCell><Badge className="rounded-full" variant={u.is_active ? "default" : "outline"}>{u.is_active ? "Yes" : "No"}</Badge></TableCell>
                      <TableCell className="space-x-1">
                        <Button size="sm" variant="ghost" onClick={()=>setEditCustomer(u)} data-testid={`cust-edit-${u.id}`}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={()=>toggleActive(u.id, !u.is_active)}>{u.is_active ? "Disable" : "Enable"}</Button>
                        <DeleteUserButton userId={u.id} userName={u.name} onDeleted={loadStatic}/>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CHEFS ------------------------------------------------------- */}
          <TabsContent value="chefs" className="mt-6">
            <ChefManagement />
          </TabsContent>

          {/* COMPLAINTS --------------------------------------------------- */}
          <TabsContent value="complaints" className="mt-6">
            <div className="card-default space-y-2">
              {complaints.length === 0 && <p className="text-sm text-muted-foreground">No complaints.</p>}
              {complaints.map(c => (
                <div key={c.id} className="p-3 rounded-xl bg-muted border border-border">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm"><b>{c.customer_name}</b> · <span className="capitalize">{c.category}</span></div>
                      <div className="text-sm text-muted-foreground mt-1">{c.message}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="rounded-full" variant="outline">{c.status}</Badge>
                      {c.status === "open" && <Button size="sm" onClick={()=>resolveComplaint(c.id)} className="rounded-full bg-secondary hover:bg-secondary/90" data-testid={`resolve-${c.id}`}>Resolve</Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* SLOT REQUESTS ----------------------------------------------- */}
          <TabsContent value="slot-requests" className="mt-6">
            <div className="card-default space-y-2">
              {slotReqs.length === 0 && <p className="text-sm text-muted-foreground">No slot requests.</p>}
              {slotReqs.map(r => (
                <div key={r.id} className="p-3 rounded-xl bg-muted border border-border">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm flex items-center gap-2"><Repeat size={14}/><b>{r.customer_name}</b> → <b>{r.new_slot}</b> {r.request_new_chef && <Badge variant="outline" className="rounded-full ml-1">new chef</Badge>}</div>
                      <div className="text-sm text-muted-foreground mt-1">{r.reason}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="rounded-full" variant="outline">{r.status}</Badge>
                      {r.status === "pending" && <>
                        <Button size="sm" variant="outline" onClick={()=>decideSlotReq(r.id, "rejected")}>Reject</Button>
                        <Button size="sm" onClick={()=>decideSlotReq(r.id, "approved")} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`approve-${r.id}`}>Approve</Button>
                      </>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="chef-changes" className="mt-6">
            <div className="card-default space-y-2">
              {chefChanges.length === 0 && <p className="text-sm text-muted-foreground">No chef-change requests.</p>}
              {chefChanges.map(r => (
                <div key={r.id} className="p-3 rounded-xl bg-muted border border-border">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm"><b>{r.customer_name}</b> wants a different chef</div>
                      <div className="text-sm text-muted-foreground mt-1">{r.reason}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="rounded-full" variant="outline">{r.status}</Badge>
                      {r.status === "pending" && <>
                        <Button size="sm" variant="outline" onClick={()=>decideChefChange(r.id, "rejected")}>Reject</Button>
                        <Button size="sm" onClick={()=>decideChefChange(r.id, "approved")} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`approve-cc-${r.id}`}>Approve</Button>
                      </>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="leaves" className="mt-6">
            <LeaveManagement
              leaves={leaves}
              chefs={chefs}
              onDecide={decideLeave}
              onReload={loadStatic}
            />
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <LeadsManagement/>
          </TabsContent>

          <TabsContent value="admins" className="mt-6">
            <AdminsTab currentUserId={currentUser?.id}/>
          </TabsContent>
        </Tabs>
      </div>
      <EditCustomerDialog
        customer={editCustomer}
        open={!!editCustomer}
        onOpenChange={(o) => { if (!o) setEditCustomer(null); }}
        onSaved={loadStatic}
      />
    </div>
  );
}
